import {Component, computed, effect, inject, input, model, output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {distinctUntilChanged} from 'rxjs/operators';

import {FieldValidators} from '../../../core/field-validators';
import {createEmptyRecordingSegment, RecordingSegmentView} from '../recording-segment.types';
import {TimecodeValueAccessorDirective} from './timecode-value-accessor.directive';
import {ErrorStateMatcher} from "@angular/material/core";

/** Show errors once the field is touched, ignoring FormGroupDirective.submitted. */
class TouchedOnlyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!control && control.invalid && control.touched;
  }
}

@Component({
  selector: 'app-recording-segment-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    TranslatePipe,
    TimecodeValueAccessorDirective,
  ],
  templateUrl: './recording-segment-form.component.html',
  styleUrl: './recording-segment-form.component.scss',
  providers: [{provide: ErrorStateMatcher, useClass: TouchedOnlyErrorStateMatcher}],
})
export class RecordingSegmentFormComponent {
  private readonly translate = inject(TranslateService);

  readonly segment = model<RecordingSegmentView | null>(null);

  readonly submitting = input(false);
  readonly disabled = input(false);

  readonly inactive = computed(() => this.submitting() || this.disabled());

  readonly submitted = output<RecordingSegmentView>();
  readonly cleared = output<void>();

  readonly form = new FormGroup(
    {
      start: new FormControl<number | null>(0, {
        validators: [
          Validators.required,
          FieldValidators.validateTimecode(),
        ],
        updateOn: 'blur',
      }),
      end: new FormControl<number | null>(0, {
        validators: [Validators.required, FieldValidators.validateTimecode()],
        updateOn: 'blur',
      }),
      remarks: new FormControl<string>(''),
    },
    {
      validators: [FieldValidators.validateTimecodeInterval('start', 'end')],
    }
  );

  constructor() {
    effect(() => {
      const segment = this.segment();

      if (!segment) {
        this.resetForm();
        return;
      }

      const isUnchanged =
        this.form.controls.start.value === segment.start &&
        this.form.controls.end.value === segment.end &&
        this.form.controls.remarks.value === (segment.remarks ?? '');

      if (isUnchanged) {
        return;
      }

      this.form.patchValue({
        start: segment.start ?? 0,
        end: segment.end ?? 0,
        remarks: segment.remarks ?? '',
      }, {emitEvent: false});
      this.startCtrl.updateValueAndValidity({emitEvent: false});
      this.form.updateValueAndValidity({emitEvent: false});
      this.form.markAsDirty();
      this.form.markAsUntouched();
    });

    // Disabled fields cannot be focused, unlike the readonly fields used while submitting
    effect(() => {
      if (this.disabled()) {
        this.form.disable({emitEvent: false});
      } else {
        this.form.enable({emitEvent: false});
      }
    });

    this.endCtrl.valueChanges.subscribe(() => {
      this.startCtrl.updateValueAndValidity({emitEvent: false});
    });

    this.form.valueChanges
      .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(value => {
        if (
          this.form.invalid ||
          value.start === null ||
          value.end === null ||
          !Number.isFinite(value.start) ||
          !Number.isFinite(value.end)
        ) {
          return;
        }

        this.segment.update(current => ({
          ...(current ?? createEmptyRecordingSegment()),
          start: value.start,
          end: value.end,
          remarks: value.remarks ?? '',
        }));
      });
  }

  get startCtrl(): FormControl<number | null> {
    return this.form.controls.start;
  }

  get endCtrl(): FormControl<number | null> {
    return this.form.controls.end;
  }

  getControlError(controlName: 'start' | 'end'): string {
    const control = this.form.controls[controlName];

    if (control.hasError('required')) {
      return this.translate.instant('TRANS__FIELD__REQUIRED');
    }

    if (control.hasError('validateTimecode')) {
      return this.translate.instant(control.errors?.['validateTimecode'].message);
    }

    if (this.form.hasError('validateTimecodeInterval')) {
      return this.translate.instant(
        this.form.errors?.['validateTimecodeInterval'].message
      );
    }

    return '';
  }

  onSubmit(): void {
    if (this.inactive()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const current = this.segment();

    if (!current) {
      return;
    }

    this.submitted.emit({
      ...current,
      start: this.form.controls.start.value!,
      end: this.form.controls.end.value!,
      remarks: this.form.controls.remarks.value ?? '',
    });
  }

  onClear(): void {
    if (this.inactive()) {
      return;
    }

    this.resetForm();
    this.segment.set(null);
    this.cleared.emit();
  }

  private resetForm(): void {
    this.form.reset({start: null, end: null, remarks: ''}, {emitEvent: false});
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }
}
